import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CButton,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormText,
  CFormTextarea,
} from "@coreui/react-pro";

import VisibilitySensor from "react-visibility-sensor";

import { sendContactEmail } from "../../../../services/sendContactEmail";
import { useAlert } from "../../../../contexts/AlertContext";
import { trackLead } from "../../../../utils/tracking";

const DEFAULT_FORM_DATA = {
  name: "",
  email: "",
  phone: "",
  service: "",
  load: "",
  isLoadingResponse: false,
};

function SectionFour() {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  const onChange = (visibility) => {
    if (visibility) {
      setIsVisible(true);
    }
  };

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});

  const formatPhoneValue = (event) => {
    const formattedPhone = event.target.value
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/g, "($1) $2")
      .replace(/(\d)(\d{4})$/, "$1-$2");

    setFormData((prev) => ({ ...prev, phone: formattedPhone }));
  };

  const { showAlert } = useAlert();

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name || formData.name.trim().length < 3)
      newErrors.name = "Informe seu nome completo (mínimo 3 caracteres).";
    if (!formData.email || !emailRegex.test(formData.email))
      newErrors.email = "Informe um e-mail válido.";
    if (!formData.phone || formData.phone.replace(/\D/g, "").length < 10)
      newErrors.phone = "Informe um telefone válido com DDD.";
    if (!formData.service || formData.service.trim() === "")
      newErrors.service = "Informe o serviço que você procura.";
    if (!formData.load || formData.load.trim() === "")
      newErrors.load = "Informe o tipo de carga.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setFormData((prev) => ({ ...prev, isLoadingResponse: true }));

    const response = await sendContactEmail(formData);

    if (response.success) {
      trackLead("lp_intermodal", {
        service: formData.service,
        load: formData.load,
      });
      if (window.fbq) {
        window.fbq("track", "Lead");
      }
      setFormData(DEFAULT_FORM_DATA);
      setErrors({});
    }

    showAlert(response.message, response.success ? "success" : "error");
    setFormData((prev) => ({ ...prev, isLoadingResponse: false }));
  };

  return (
    <VisibilitySensor onChange={onChange} partialVisibility minTopValue={100}>
      <div id="contact" className={`sectionFour ${isVisible ? "visible" : ""}`}>
        <div className="sectionFourText">
          <h1 className={`${isVisible ? "visible" : ""}`}>
            {t("lp1_form_title")}
          </h1>
          <p>{t("lp1_form_text")}</p>
        </div>
        <CForm id="intermodal2026Form" className={`${isVisible ? "visible" : ""}`}>
          <div>
            {/* Nome completo */}
            <CFormLabel className="me-2 labelName">
              {t("lp1_form_name")}
            </CFormLabel>
            <CFormInput
              type="text"
              maxLength={80}
              minLength={3}
              value={formData.name}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, name: e.target.value }));
                setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              className="me-2"
              placeholder=""
              invalid={!!errors.name}
            />
            {errors.name && <CFormText className="text-danger">{errors.name}</CFormText>}

            {/* E-mail */}
            <CFormLabel>{t("lp1_form_email")}</CFormLabel>
            <CFormInput
              type="email"
              autoComplete="off"
              maxLength={60}
              className="me-2"
              placeholder=""
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
                setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              invalid={!!errors.email}
            />
            {errors.email && <CFormText className="text-danger">{errors.email}</CFormText>}

            {/* Telefone */}
            <CFormLabel>{t("lp1_form_phone")}</CFormLabel>
            <CFormInput
              type="tel"
              autoComplete="off"
              maxLength={15}
              onChange={(e) => {
                formatPhoneValue(e);
                setErrors((prev) => ({ ...prev, phone: undefined }));
              }}
              className="me-2"
              placeholder=""
              value={formData.phone}
              invalid={!!errors.phone}
            />
            {errors.phone && <CFormText className="text-danger">{errors.phone}</CFormText>}

            {/* Qual serviço você procura? */}
            <CFormLabel>Qual serviço você procura?</CFormLabel>
            <CFormInput
              type="text"
              autoComplete="off"
              maxLength={100}
              className="me-2"
              placeholder=""
              value={formData.service}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, service: e.target.value }));
                setErrors((prev) => ({ ...prev, service: undefined }));
              }}
              invalid={!!errors.service}
            />
            {errors.service && <CFormText className="text-danger">{errors.service}</CFormText>}

            {/* Qual o tipo de carga? */}
            <CFormLabel>Qual o tipo de carga?</CFormLabel>
            <CFormInput
              type="text"
              autoComplete="off"
              maxLength={100}
              className="me-2"
              placeholder=""
              value={formData.load}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, load: e.target.value }));
                setErrors((prev) => ({ ...prev, load: undefined }));
              }}
              invalid={!!errors.load}
            />
            {errors.load && <CFormText className="text-danger">{errors.load}</CFormText>}

            <CButton
              className="mt-4"
              onClick={handleSubmit}
              disabled={formData.isLoadingResponse}
            >
              {formData.isLoadingResponse
                ? t("lp1_form_sending")
                : t("lp1_form_send")}
            </CButton>
          </div>
        </CForm>
      </div>
    </VisibilitySensor>
  );
}

export default SectionFour;
